import { deslugify, produceKeyedMapping } from '@percept/utils'
import { vodafoneMarkets } from 'vodafoneMarkets'
import { BenchmarkDatum } from './BenchmarkingQuadrantChart'
import { ProviderBenchmarks } from '@percept/hooks'
import { UserIdRestrictions } from '@percept/app-components'
import { sortBy, uniq } from 'lodash-es'
import { ReportProviderV2 } from '@percept/types'


const segmentLabels: Record<string, string> = {
  'YOUTUBE': 'YouTube',
  'TIKTOK': 'TikTok',
}

export const getSegmentLabel = (segment: string): string => (
  segmentLabels[segment] || deslugify(segment)
)

export const defaultProviderSegmentMap: Partial<Record<ReportProviderV2, string>> = {
  GOOGLE_ADS: 'SEARCH',
}

export const vodafoneMarketsById = produceKeyedMapping(vodafoneMarkets, 'id')

export const temporaryBenchmarkingUserRestrictions: UserIdRestrictions = {
  VODAFONE: {
    PROD: [
      '3225e444-d001-700a-9654-3793e59a5f4e',
      'e2159434-5011-7061-cfba-c29b8451df3c',

      '18395616-da1c-4130-9e96-f65e2c250604',
      '52856444-50d1-7069-c275-6820abee1f76',
      '929534f4-0021-70f2-0e2d-f2c24d961109',

      '7aa15908-6993-44c0-b6dd-6dbace1cdfa2',

      '4215f494-c0c1-7092-b164-81a852b6eee5',

      'b27594a4-4021-7005-190e-12da37ebe76d',

      'f24aec63-7e1e-4c7b-aff4-dd2456cc77af',
      '9ff5c4e2-881b-49e4-a47b-6437ab9dcc1c',

      '2ddafd96-e2e3-48ca-9c06-d31dc3f7c993',
      '765f460f-63f6-4a98-a9dd-d44e8f8d0e59',
    ],
  }
}

export const SAMPLE_DATA: ProviderBenchmarks = {
  'provider': 'GOOGLE_ADS',
  'current_month': '2025-06-01',
  'prior_month': '2025-05-01',
  'results': [
    {
      'provider_segment': {
        'segment': 'GOOGLE_SEARCH',
      },
      'results': [
        {
          'org_unit_id': '31774511989840',
          'org_unit_name': 'Vodafone UK',
          'outcomes': {
            'current_rate': 0.05,
            'prior_rate': 0.04,
            'change': 0.25,
          },
          'effectiveness': {
            'rate_type': 'cpc',
            'competitor_group': {
              'group_id': 'str_int',
              'group_name': 'name',
              'current_rate': 0.0244704,
              'prior_rate': 0.024,
              'change': 0.0196,
            },
            'org_activity': {
              'current_rate': 0.304767,
              'prior_rate': 0.03,
              'change': 0.1589,
            },
            'relative_change': 0.1393, 
          },
        }
      ]
    },
    {
      'provider_segment': {
        'segment': 'GOOGLE_DISPLAY',
      },
      'results': [
        {
          'org_unit_id': '31774511989840',
          'org_unit_name': 'Vodafone UK',
          'outcomes': {
            'current_rate': 0.0010875,
            'prior_rate': 0.00087,
            'change': 0.25,
          },
          'effectiveness': {
            'rate_type': 'cpm',
            'competitor_group': {
              'group_id': 'str_int',
              'group_name': 'name',
              'current_rate': 0.0244704,
              'prior_rate': 0.024,
              'change': 0.0196,
            },
            'org_activity': {
              'current_rate': 0.304767,
              'prior_rate': 0.03,
              'change': 0.1589,
            },
            'relative_change': 0.1393, 
          },
        }
      ]
    }
  ]
}

const testChangePoints: Record<string, [number, number]> = {
  // Midpoints
  UK: [0.5, 0.5],
  DE: [-0.5, 0.5],
  PT: [0.5, -0.5],
  ZA: [-0.5, -0.5],
  // Far corners
  CZ: [1, 1],
  AL: [-1, -1],
  RO: [1, -1],
  TR: [-1, 1],
  // Center
  EG: [0, 0],
}

const sampleMarketIsoCodes = Object.keys(testChangePoints)
const marketsByIso = produceKeyedMapping(vodafoneMarkets, 'iso_code')

export const TEST_DATA: ProviderBenchmarks = {
  'provider': 'GOOGLE_ADS',
  'current_month': '2025-06-01',
  'prior_month': '2025-05-01',
  results: SAMPLE_DATA.results.map( r => ({
    ...r,
    results: sampleMarketIsoCodes.map( isoCode => {
      const market = marketsByIso[isoCode]
      return {
        org_unit_id: market.id,
        org_unit_name: market.name,
        outcomes: {
          current_rate: 0,
          prior_rate: 0,
          change: testChangePoints[isoCode][0]
        },
        effectiveness: {
          rate_type: 'cpm',
          competitor_group: {
            group_id: 'str_int',
            group_name: 'name',
            current_rate: 0.0244704,
            prior_rate: 0.024,
            change: 0.0196,
          },
          org_activity: {
            current_rate: 0.304767,
            prior_rate: 0.03,
            change: 0.1589,
          },
          relative_change: testChangePoints[isoCode][1],
        }
      }
    })
  }))
}

export type BenchmarkDataset = {
  key: string
  label: string
  costEffectivenessMetric?: string | null
  data: BenchmarkDatum[]
}

export const getBenchmarkDatasets = (data: ProviderBenchmarks): BenchmarkDataset[] => {
  return sortBy(
    data.results.map( d => {
      const costEffectiveMetrics = uniq(d.results.map( r => r.effectiveness.rate_type ))
      const costEffectivenessMetric = (
        costEffectiveMetrics.length === 1 ?
          costEffectiveMetrics[0] :
          null
      )
      return {
        key: d.provider_segment.segment,
        label: getSegmentLabel(d.provider_segment.segment),
        costEffectivenessMetric,
        data: d.results.map( r => ({
          org_unit_id: r.org_unit_id,
          effectiveness: r.effectiveness.relative_change,
          outcomes: r.outcomes.change,
        }))
      }
    }),
    'label'
  )
}
